<template>
  <div>
    <v-row>
      <h2>{{ $t('features.nursery-web-school-setting-group') }}</h2>
      <v-spacer />
      <v-btn @click="clickCreateBtn" color="primary" variant="flat">
        <v-icon start>mdi-plus</v-icon>
        {{ $t('settings.createGroup') }}
      </v-btn>
    </v-row>

    <!-- searchFilters -->
    <v-row>
      <v-col cols="3" class="pl-0">
        <v-text-field
          v-model="searchFilters.year"
          :suffix="$t('mailbox.schoolYear')"
          :label="$t('common.fiscalYear')"
          type="number"
          hide-details
          variant="outlined"
          density="compact"
        >
        </v-text-field>
      </v-col>
      <v-col cols="9" align-self="center">
        <div v-if="loading">
          <v-progress-circular
            color="primary"
            indeterminate
          ></v-progress-circular>
        </div>
      </v-col>
    </v-row>

    <!-- group list -->
    <div class="pt-6" v-if="groups.length > 0">
      <v-list>
        <v-list-item v-for="(item, i) in groups" :key="i" class="pa-2">
          <v-card @click="clickItem(item)" variant="outlined" link width="100%" color="grey">
            <v-card-text class="text-black">
              <v-row>
                <v-col cols="6">
                  {{ item.name }}
                </v-col>
                <v-col cols="3">
                  {{ item.year + " "+ $t('mailbox.schoolYear') }}
                </v-col>
                <v-col cols="3">
                  {{ $t('settings.createdDate') + ': ' + formatDate(item.createdAt) }}
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-list-item>
      </v-list>
    </div>

    <div class="pt-6" v-else>
      <h5>{{ $t('settings.noGroups') }}</h5>
    </div>

    <!-- dialog -->
    <v-dialog v-model="dialog" max-width="620px">
      <v-card>
        <v-card-title>
          {{ isEdit ? $t('settings.groupSettings') : $t('settings.createGroup') }}
        </v-card-title>
        <v-card-text>
          <v-container>
            <v-row align="center">
              <v-col cols="6">
                <div>{{ $t('settings.targetYear') }}</div>
              </v-col>
              <v-col cols="6">
                <v-text-field
                  v-model="group.year"
                  type="number"
                  :suffix="$t('mailbox.schoolYear')"
                  variant="outlined"
                  hide-details=""
                  density="compact"
                ></v-text-field>
              </v-col>
            </v-row>
            <v-row align="center">
              <v-col cols="6">
                <div>{{ $t('settings.groupName') }}</div>
              </v-col>
              <v-col cols="6">
                <v-text-field
                  v-model="group.name"
                  variant="outlined"
                  hide-details=""
                  density="compact"
                ></v-text-field>
              </v-col>
            </v-row>
          </v-container>
        </v-card-text>
        <v-card-actions>
          <v-container>
            <v-row>
              <v-btn @click="dialog = false" color="grey" text>
                {{ $t('common.cancel') }}
              </v-btn>
              <v-spacer />
              <v-btn
                v-if="isEdit"
                @click="deleteGroup()"
                :loading="loading"
                class="mr-2"
                color="error"
                variant="flat"
              >
                {{ $t('common.delete') }}
              </v-btn>
              <v-btn
                v-if="isEdit"
                @click="updateGroup"
                :loading="loading"
                color="primary"
                variant="flat"
              >
                {{ $t('common.save') }}
              </v-btn>
              <v-btn
                v-else
                @click="createGroup"
                :loading="loading"
                color="primary"
                variant="flat"
              >
                {{ $t('common.create') }}
              </v-btn>
            </v-row>
          </v-container>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script lang="ts">
import ApiGroup from "@/api/ApiGroup";
import LocalDataService from "@/service/LocalDataService";
import { groupModule } from "@/store/dataModules/groupModule";
import { snackbarModule } from "@/store/viewModules/snackbarModule";
import CommonUtil from "@/utils/CommonUtil";
import { NurseryGroupListResponse, NurseryGroupResponse } from "chaild-api/lib";
import dayjs from "dayjs";
import { defineComponent } from "vue";

export default defineComponent({
  name: "GroupSetting",

  async mounted() {
    await this.listGroups();
  },

  watch: {
    searchFilters: {
      deep: true,
      handler() {
        this.listGroups();
      },
    },
  },

  data() {
    return {
      loading: false,
      dialog: false,
      isEdit: false,
      searchFilters: {
        year: CommonUtil.getFiscalYear(),
      },
    };
  },

  computed: {
    groups: function (): NurseryGroupListResponse | null {
      return groupModule.groups;
    },
    group: function (): NurseryGroupResponse | null {
      return groupModule.group;
    },
  },

  methods: {
    async listGroups() {
      const nurseryId = LocalDataService.getNurseryId();
      if (nurseryId) {
        await groupModule.listGroups({
          nurseryId,
          ...this.searchFilters,
        });
      }
    },
    clickItem(value: NurseryGroupResponse) {
      this.isEdit = true;
      groupModule.setGroup(value);
      this.dialog = true;
    },
    clickCreateBtn() {
      this.isEdit = false;
      groupModule.setGroup({
        year: Number(this.searchFilters.year),
        groupId: 0,
        name: "",
        createdAt: "",
        updatedAt: null,
      });
      this.dialog = true;
    },
    async createGroup() {
      const nurseryId = LocalDataService.getNurseryId();
      if (nurseryId && groupModule.group) {
        this.loading = true;
        const response = await ApiGroup.createGroup({
          nurseryId,
          year: String(groupModule.group.year),
          name: groupModule.group.name,
        }).catch(() => (this.loading = false));
        if (response) {
          this.dialog = false;
          snackbarModule.snackOn({
            message: "Group created successfully",
          });
          await this.listGroups();
        }
        this.loading = false;
      }
    },
    async updateGroup() {
      if (groupModule.group) {
        this.loading = true;
        const response = await ApiGroup.updateGroup({
          groupId: groupModule.group.groupId,
          year: String(groupModule.group.year),
          name: groupModule.group.name,
        }).catch(() => (this.loading = false));
        if (response) {
          this.dialog = false;
          snackbarModule.snackOn({
            message: "Saved successfully",
          });
          await this.listGroups();
        }
        this.loading = false;
      }
    },
    async deleteGroup() {
      if (groupModule.group) {
        const answer = confirm("Are you sure you want to delete this group?");
        if (answer) {
          this.loading = true;
          const response = await ApiGroup.deleteGroup({
            groupId: groupModule.group.groupId,
          }).catch(() => (this.loading = false));
          if (response) {
            this.dialog = false;
            snackbarModule.snackOn({
              message: "Deleted successfully",
            });
            await this.listGroups();
          }
          this.loading = false;
        }
      }
    },
    formatDate(value: string) {
      if (!value) {
        return "";
      }
      return dayjs(value).format("DD/MM/YYYY");
    },
  },
});
</script>
